import { useEffect, useRef, useMemo } from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { CardList, CardListItems } from '@components/card-list';
import { EmptyState } from '@components/empty-state';
import { IconApplicants } from '@components/icons/applicants';
import { useScopedTranslate } from '@locale/translate';
import { getVisibleHeight, throttle } from '@utils/browser';

import { LoadingMore } from 'components/LoadingMore';
import { PipelineCard } from 'components/pipeline-card';

export const Stage = observer(({ stage, selectedId = -1 }) => {
  const { t } = useScopedTranslate(locale);

  if (stage.state === 'loaded' && stage.applications.length === 0) {
    return (
      <EmptyState
        variant="small"
        orientation="vertical"
        skipMargin
        gap={4}
        icon={<IconApplicants />}
        content={t('empty')}
      />
    );
  }

  return (
    <CardList>
      <List stage={stage} selectedId={selectedId} />
      {stage.nextState === 'loading' && <LoadingMore />}
    </CardList>
  );
});

const List = observer(({ stage, selectedId }) => {
  const applications = stage.applications;
  const timeoutRef = useRef(null);
  const ref = useRef();

  const onScroll = useMemo(
    () =>
      throttle((e) => {
        if (!stage.next) return;
        const el = e.currentTarget;
        if (!el || el.scrollTop <= 0) return;

        const maxHeight = el.scrollHeight;
        const heightWithScroll = el.clientHeight + el.scrollTop;
        if (maxHeight - heightWithScroll < 300) {
          stage.loadNext();
        }
      }, 200),
    [stage]
  );

  useEffect(() => {
    if (applications.length < 20 && stage.next) {
      timeoutRef.current = setTimeout(() => {
        stage.loadNext();
      }, 3000);
      return () => {
        clearTimeout(timeoutRef.current);
      };
    }
  });

  useEffect(() => {
    window.requestAnimationFrame(() => {
      if (ref && ref.current) {
        const ht = getVisibleHeight() - ref.current.getBoundingClientRect().top - 24;
        ref.current.style.maxHeight = ht + 'px';
      }
    });
  });

  return (
    <PaddedCardListItems ref={ref} onScroll={onScroll}>
      {applications.map((application) => (
        <Item
          key={application.id}
          application={application}
          selected={selectedId === application.id}
        />
      ))}
    </PaddedCardListItems>
  );
});

const PaddedCardListItems = styled(CardListItems)({
  padding: '0.5rem',
});

const locale = {
  en: { empty: 'No applicants in selected stage' },
  is: { empty: 'Engir umsækjendur í völdum dálki' },
};

const Item = observer(({ application, selected }) => {
  useEffect(() => {
    if (selected) {
      const el = document.getElementById('application-' + application.id);
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selected, application.id]);

  return <PipelineCard application={application} active={selected} />;
});
